import { useTemplateFolders } from "@/queries/forms/template-folder.query.ts";
import { TemplateFolderModule } from "@/types/forms/form-template-folder.types.ts";
import { Button, Modal, ModalProps, Select, Stack, Text, useModals } from "@skailer-com/uikit";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface TemplateFolderSelectModalProps extends ModalProps {
  module: TemplateFolderModule;
  modalTitle: string;
  text?: string;
  acceptTitle: string;
  currentFolderId?: number;
  onConfirm: (folderId: number) => Promise<void>;
}

export function TemplateFolderSelectModal({
  module,
  modalTitle,
  text,
  acceptTitle,
  currentFolderId,
  onConfirm,
  ...props
}: TemplateFolderSelectModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { data, isLoading, isError } = useTemplateFolders(module);
  const folders = data?.filter((folder) => folder.id !== currentFolderId);
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>();
  const folderId =
    selectedFolderId === undefined ? (folders?.[0]?.id ?? null) : (selectedFolderId ?? null);
  const [isSaving, setIsSaving] = useState(false);
  const noOtherFolders = !isLoading && !isError && folders?.length === 0;
  const canSubmit = !isLoading && !isError && folders?.some((folder) => folder.id === folderId);

  const onSubmit = async () => {
    if (!canSubmit || folderId === null || isSaving) return;
    setIsSaving(true);
    try {
      await onConfirm(folderId);
      close(props.id);
    } catch {
      return;
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      {...props}
      title={modalTitle}
      withCloser
      actions={
        <Stack>
          {noOtherFolders ? (
            <Button size="lg" onClick={() => close(props.id)}>
              {t("common:close")}
            </Button>
          ) : (
            <Button size="lg" onClick={onSubmit} disabled={!canSubmit} isLoading={isSaving}>
              {acceptTitle}
            </Button>
          )}
        </Stack>
      }
    >
      <Stack gap="md">
        {text && <Text>{text}</Text>}
        {!noOtherFolders && (
          <Select
            label={t("core:folder")}
            emptyTitle={t("common:select")}
            value={folderId}
            options={folders}
            isLoading={isLoading}
            required
            onChange={(value) => setSelectedFolderId(typeof value === "number" ? value : null)}
          />
        )}
        {isError && <Text className="text-sm text-danger">{t("common:folders_load_failed")}</Text>}
        {noOtherFolders && (
          <Text className="text-sm text-secondary">{t("common:no_other_folders")}</Text>
        )}
      </Stack>
    </Modal>
  );
}
